import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SessionState } from 'sip.js';
import * as lib from '~/dependencies';
import messagesToast from '~/validations/messagesNotifications';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import { useCallHold, useDetectMicrophone } from '~/hooks';
import {
  log,
  isValidInviteTarget,
  sdpModifierCodec,
  mediaStreamMonitor,
} from '~/helpers';
import { IExtra, ActionControl } from '~/interfaces';
import useTypedSelector from './useTypedSelector';

export default function useControlSessions() {
  // HOOKS
  const dispatch = useDispatch();
  const history = useHistory();
  const microphone = useDetectMicrophone();
  const setCallHold = useCallHold(false);

  // SELECTORS
  const sessionActive = useTypedSelector(state => state.callCurrent.session);
  const sessions = useTypedSelector(state => state.callCurrent.data);
  const ua = useTypedSelector(state => state.phone.ua);
  const systemLocalVideo = useTypedSelector(state => state.system?.localVideo);
  const statusSystem = useTypedSelector(state => state.system?.status);

  //  General function to control functions of system voip
  const controlSessions = useCallback(
    async (
      id: string | null = null,
      action: ActionControl,
      value: string | number = '',
      extra: IExtra | null = null
    ) => {
      const { session, id: sessionId, status: sessionStatus } = {
        ...sessions?.find(ses => ses.id === id),
      };
      const isMicrophoneReachable = microphone();

      switch (action) {
        case 'invite':
          if (ua?.isRegistered() && isValidInviteTarget(value)) {
            isMicrophoneReachable.then(async res => {
              try {
                if (res) {
                  const newSession = ua.makeCall(value, {
                    sessionDescriptionHandlerOptions: {
                      constraints: {
                        audio: true,
                      },
                      iceGatheringTimeout: 700,
                    },
                    sessionDescriptionHandlerModifiers: [sdpModifierCodec],
                  });

                  if (extra?.name || extra?.contactId) {
                    dispatch(
                      actions.addNewSession(newSession, {
                        name: extra.name,
                        contactId: extra.contactId,
                        invite: true,
                      })
                    );
                  } else if (extra?.waitingToMergeConf) {
                    dispatch(
                      actions.addNewSession(newSession, {
                        waitingToMergeConf: extra.waitingToMergeConf,
                        invite: true,
                      })
                    );
                  } else if (extra?.transferOff) {
                    dispatch(actions.updateCall('transferOff', id));
                    dispatch(
                      actions.addNewSession(newSession, { invite: true })
                    );
                  } else {
                    dispatch(
                      actions.addNewSession(newSession, { invite: true })
                    );
                  }

                  if (history?.location?.pathname === '/app/dialPad') {
                    setTimeout(() => {
                      history.push('/app/displayCall');
                    });
                  }
                } else {
                  lib.ToastMessage(
                    'warn',
                    messagesToast().SYSTEM.microphone.error
                  );
                }

                // Clean number from dial after call started
                dispatch(actions.numberToTransfer());
              } catch (err) {
                log.error('Failed to execute UA.makeCall.', err);
              }
            });
          } else if (
            !ua?.isRegistered() &&
            statusSystem !== actionTypes.STATUS.BUSY
          ) {
            // Set Unavailable while trying to register
            dispatch(actions.statusSystem('Unavailable'));
          } else {
            lib.ToastMessage(
              'warn',
              `${messagesToast().NOTIFICATIONS.call.invalidPhoneNumber}`
            );
          }
          break;
        case 'acceptOutgoing':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'accept':
          if (sessionActive?.state === SessionState.Established) {
            setCallHold(sessionActive, true);
            dispatch(actions.updateCall('hold', sessionActive?.id));
          }
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'finish':
          if (session) {
            if (sessionStatus === actionTypes.CALL_CONFERENCE) {
              sessions.forEach(e => {
                dispatch(actions.updateCall(action, e.id));
              });
            } else {
              dispatch(actions.updateCall(action, sessionId));
            }
          }
          break;
        case 'cancel':
          if (session) {
            dispatch(actions.updateCall(action, sessionId));
          }
          break;
        case 'reject':
          dispatch(actions.PlayRing('endTone', true));
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'reinvite':
          dispatch(actions.updateCall(action, sessionId));
          controlSessions(sessionId, 'unhold');
          break;
        case 'blindtransfer':
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'attendedtransfer':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'mute':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'unmute':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'transferOn':
          controlSessions(sessionId, 'hold');
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'transferOff':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'waitingTransfer':
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'dtmfAction':
          if (!value) {
            dispatch(actions.updateCall('dtmf', sessionId, ''));
          }
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'hold':
          if (session) {
            setCallHold(session, true);
          }
          break;
        case 'unhold':
          if (session) {
            setCallHold(session, false);
          }
          break;
        case 'clean':
          dispatch(
            actions.callsCurrent(sessions.filter(s => s.finished !== true))
          );
          break;
        case 'dtmf':
          try {
            const dtmfValue = ua.formatDtmfCode(value);
            if (action) {
              if (dtmfValue.slice(-1) && !extra?.erase) {
                if (extra?.lastDigit) {
                  const lastDigit = ua.formatDtmfCode(extra.lastDigit);
                  ua.sendDtmf(lastDigit, session);
                } else {
                  ua.sendDtmf(dtmfValue.slice(-1), session);
                }
              }
              dispatch(actions.updateCall(action, sessionId, dtmfValue));
            } else {
              dispatch(actions.updateCall(action, sessionId, ''));
            }
          } catch (err) {
            log.info('Failed to send DTMF', err);
          }
          break;
        case 'dtmfDigit':
          try {
            const dtmfValue = ua.formatDtmfCode(value);

            if (extra?.lastDigit) {
              const lastDigit = ua.formatDtmfCode(extra.lastDigit);
              ua.sendDtmf(lastDigit, session);
              dispatch(actions.updateCall('dtmf', sessionId, dtmfValue));
            }
          } catch (err) {
            log.error('Failed to send DTMF', err);
          }
          break;
        case 'conferenceOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'conferenceOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'announcementOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'tagsOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'tagsOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'notesOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'notesOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'announcementOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'mergeCallConference':
          try {
            const callIdPeer2 = sessions.findIndex(
              e => e.waitingToMergeConf === true
            );
            const callConfSession = session;
            const callConfSession2 = sessions[callIdPeer2]?.session;

            dispatch(actions.updateCall(action, sessionId, ''));

            if (callConfSession && callConfSession2) {
              const pcSession1: RTCPeerConnection =
                // @ts-ignore
                callConfSession.sessionDescriptionHandler?.peerConnection;
              const pcSession2: RTCPeerConnection =
                // @ts-ignore
                callConfSession2.sessionDescriptionHandler?.peerConnection;

              const mediaStreamSession1 = new MediaStream();
              const mediaStreamSession2 = new MediaStream();
              const mediaStreamSession1Recv = new MediaStream();
              const mixer = new MediaStream();

              // To tracker midias states
              mediaStreamMonitor.setMediaStreams({
                mediaStreamSession1,
                mediaStreamSession2,
                mediaStreamSession1Recv,
                mixer,
              });

              let rtcReceiverSession1: RTCRtpSender;
              let rtcReceiverSession2: RTCRtpSender;

              pcSession1.getSenders().forEach(sender => {
                rtcReceiverSession1 = sender;
                if (sender.track) {
                  mediaStreamSession1.addTrack(sender.track);
                }
              });

              pcSession2.getSenders().forEach(sender => {
                rtcReceiverSession2 = sender;
              });

              pcSession1.getReceivers().forEach(receiver => {
                mediaStreamSession1Recv.addTrack(receiver.track);
                mixer.addTrack(receiver.track);
              });

              pcSession2.getReceivers().forEach(receiver => {
                mediaStreamSession2.addTrack(receiver.track);
                mixer.addTrack(receiver.track);
              });

              const audioContext = new AudioContext();
              const mediaStreamSource = audioContext.createMediaStreamSource(
                mediaStreamSession1
              );
              const participant1 = audioContext.createMediaStreamSource(
                mediaStreamSession1Recv
              );
              const participant2 = audioContext.createMediaStreamSource(
                mediaStreamSession2
              );

              // Send the stream to MediaStream, which needs to be connected to PC
              const destination_participant1 = audioContext.createMediaStreamDestination();
              const destination_participant2 = audioContext.createMediaStreamDestination();

              mediaStreamSource.connect(destination_participant1);
              participant1.connect(destination_participant1);

              mediaStreamSource.connect(destination_participant2); // Send local stream to the mixer
              participant2.connect(destination_participant2); // add all participants to the mix

              const track1 = destination_participant1.stream.getTracks();
              const track2 = destination_participant2.stream.getTracks();

              // @ts-ignore
              rtcReceiverSession1.replaceTrack(track2[0]);
              // @ts-ignore
              rtcReceiverSession2.replaceTrack(track1[0]);

              dispatch(actions.remoteVideo(mixer));
            }
          } catch (err) {
            log.error(`Failed to merge calls: `, err);
          }
          break;
        case 'splitCallConference':
          try {
            const callIdPeer2 = sessions.findIndex(
              e => e.waitingToMergeConf === true
            );

            // Get peer sessions
            const callConfSession = session;
            const callConfSession2 = sessions[callIdPeer2]?.session;

            if (callConfSession && callConfSession2) {
              const pcSession1: RTCPeerConnection =
                // @ts-ignore
                callConfSession.sessionDescriptionHandler?.peerConnection;
              const pcSession2: RTCPeerConnection =
                // @ts-ignore
                callConfSession2.sessionDescriptionHandler?.peerConnection;

              const mediaStreamSession2 = new MediaStream();

              let rtcReceiverSession1: RTCRtpSender;
              let rtcReceiverSession2: RTCRtpSender;

              // RTCRtpSender session 1
              pcSession1.getSenders().forEach(sender => {
                rtcReceiverSession1 = sender;
              });

              // RTCRtpSend session 2
              pcSession2.getSenders().forEach(sender => {
                rtcReceiverSession2 = sender;
              });

              // MediaStreamTrack session 2
              pcSession2.getReceivers().forEach(receiver => {
                mediaStreamSession2.addTrack(receiver.track);
              });

              const localMediaStreamTrack = systemLocalVideo.getTracks()[0];

              // FIXME: Ts says we are using before assign, we should review in the future
              // @ts-ignore
              rtcReceiverSession1.replaceTrack(localMediaStreamTrack);
              // @ts-ignore
              rtcReceiverSession2.replaceTrack(localMediaStreamTrack);

              dispatch(actions.remoteVideo(mediaStreamSession2));

              if (session) {
                setCallHold(session, true);

                setTimeout(() => {
                  dispatch(actions.updateCall(action, sessionId, ''));
                  // @ts-ignore
                  clearTimeout(this);
                }, 1000);
              }
            }
          } catch (err) {
            log.error(`Failure to split call conference.`, err);
          }
          break;
        default:
          break;
      }
    },
    [
      sessions,
      microphone,
      ua,
      statusSystem,
      dispatch,
      setCallHold,
      sessionActive,
      systemLocalVideo,
      history,
    ]
  );

  return controlSessions;
}
